// =====================================================
// IMPULSIVE TRADER BENCHMARK SERVICE
// Simulates what would happen if user took ALL trades
// ignoring NO_TRADE/WAIT decisions from AI
// =====================================================

import { supabase } from '@/integrations/supabase/client';
import type { CounterfactualTracking, CounterfactualTrackingRow } from '@/types/counterfactual';
import { rowToCounterfactual } from '@/types/counterfactual';
import type { 
  ImpulsiveTraderStats, 
  ImpulsiveTraderOutcome,
  ImpulsiveTradeSimulation 
} from '@/types/impulsiveTrader';
import { 
  calculateImpulsiveOutcome, 
  DEFAULT_RISK_PER_TRADE_USD 
} from '@/types/impulsiveTrader';

/**
 * Get all counterfactual trackings for impulsive trader simulation
 */
export async function getImpulsiveTraderSimulations(
  days?: number
): Promise<ImpulsiveTradeSimulation[]> {
  try {
    let query = supabase
      .from('counterfactual_tracking')
      .select(`
        *,
        journal_entries!inner (
          execution_permission
        )
      `)
      .eq('tracking_status', 'COMPLETED')
      .not('capital_preserved_r', 'is', null);

    // Add date filter if specified
    if (days) {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);
      query = query.gte('created_at', startDate.toISOString());
    }

    const { data, error } = await query;

    if (error) {
      console.error('[ImpulsiveTraderService] Error fetching simulations:', error);
      return [];
    }

    return (data || []).map((row: any) => {
      const tracking = rowToCounterfactual(row as CounterfactualTrackingRow);
      const decision = row.journal_entries?.execution_permission as 'NO_TRADE' | 'WAIT';
      const outcome = calculateImpulsiveOutcome(tracking, decision || 'NO_TRADE');
      return { tracking, outcome };
    });
  } catch (err) {
    console.error('[ImpulsiveTraderService] Exception fetching simulations:', err);
    return [];
  }
}

/**
 * Get impulsive trader outcome for a specific journal entry
 */
export async function getImpulsiveOutcomeForEntry(
  journalEntryId: string
): Promise<ImpulsiveTraderOutcome | null> {
  try {
    const { data, error } = await supabase
      .from('counterfactual_tracking')
      .select(`
        *,
        journal_entries!inner (
          execution_permission
        )
      `)
      .eq('journal_entry_id', journalEntryId)
      .eq('tracking_status', 'COMPLETED')
      .maybeSingle();

    if (error || !data) {
      return null;
    }

    const tracking = rowToCounterfactual(data as CounterfactualTrackingRow);
    const decision = (data as any).journal_entries?.execution_permission as 'NO_TRADE' | 'WAIT';
    return calculateImpulsiveOutcome(tracking, decision || 'NO_TRADE');
  } catch (err) {
    console.error('[ImpulsiveTraderService] Exception fetching outcome:', err);
    return null;
  }
}

/**
 * Calculate aggregated impulsive trader statistics
 */
export async function getImpulsiveTraderStats(
  days?: number
): Promise<ImpulsiveTraderStats | null> {
  try {
    // Get all simulations
    const simulations = await getImpulsiveTraderSimulations(days);
    
    if (simulations.length === 0) {
      return {
        totalNoTradeDecisions: 0,
        decisionsWithTracking: 0,
        impulsivePnlR: 0,
        impulsivePnlUsd: 0,
        impulsiveWins: 0,
        impulsiveLosses: 0,
        impulsiveWinRate: 0,
        actualPnlR: 0,
        actualPnlUsd: 0,
        disciplinePremiumR: 0,
        disciplinePremiumUsd: 0,
        avoidedDrawdownPercent: 0,
        lossesAvoided: 0,
        totalLossAvoidedUsd: 0,
        hasInstitutionalEdge: false,
      };
    }

    // Get total NO_TRADE/WAIT decisions count
    let countQuery = supabase
      .from('journal_entries')
      .select('id', { count: 'exact', head: true })
      .in('execution_permission', ['NO_TRADE', 'WAIT']);

    if (days) {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);
      countQuery = countQuery.gte('created_at', startDate.toISOString());
    }

    const { count: totalNoTradeDecisions } = await countQuery;

    // Calculate impulsive trader stats
    let impulsivePnlR = 0;
    let impulsivePnlUsd = 0;
    let impulsiveWins = 0;
    let impulsiveLosses = 0;
    let lossesAvoided = 0;
    let totalLossAvoidedUsd = 0;

    simulations.forEach(({ outcome }) => {
      if (outcome.hypotheticalRMultiple !== null) {
        impulsivePnlR += outcome.hypotheticalRMultiple;
        impulsivePnlUsd += outcome.hypotheticalPnlUsd || 0;

        if (outcome.hypotheticalOutcome === 'WIN') {
          impulsiveWins++;
        } else if (outcome.hypotheticalOutcome === 'LOSS') {
          impulsiveLosses++;
          lossesAvoided++;
          totalLossAvoidedUsd += outcome.lossAvoided || 0;
        }
      }
    });

    // Get actual trader PnL (from completed trades)
    let actualQuery = supabase
      .from('journal_entries')
      .select('outcome_r_multiple')
      .in('execution_permission', ['TRADE', 'TRADE_APPROVED'])
      .eq('outcome_status', 'COMPLETED')
      .not('outcome_r_multiple', 'is', null);

    if (days) {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);
      actualQuery = actualQuery.gte('created_at', startDate.toISOString());
    }

    const { data: actualTrades } = await actualQuery;
    
    let actualPnlR = 0;
    (actualTrades || []).forEach((trade: any) => {
      actualPnlR += trade.outcome_r_multiple || 0;
    });
    const actualPnlUsd = actualPnlR * DEFAULT_RISK_PER_TRADE_USD;

    // Calculate discipline premium (what discipline saved)
    // Discipline Premium = Actual PnL - Impulsive PnL
    // If positive: discipline saved money (impulsive trader lost more / gained less)
    // Since impulsive includes losses that were avoided, we need:
    // Discipline Premium = -impulsivePnlR (if impulsive lost, we saved that)
    const disciplinePremiumR = -impulsivePnlR;
    const disciplinePremiumUsd = -impulsivePnlUsd;

    // Calculate avoided drawdown percentage
    const totalHypotheticalRisk = simulations.length * DEFAULT_RISK_PER_TRADE_USD;
    const avoidedDrawdownPercent = totalHypotheticalRisk > 0 
      ? (totalLossAvoidedUsd / totalHypotheticalRisk) * 100 
      : 0;

    const impulsiveWinRate = (impulsiveWins + impulsiveLosses) > 0
      ? (impulsiveWins / (impulsiveWins + impulsiveLosses)) * 100
      : 0;

    // Has institutional edge if discipline premium is positive
    const hasInstitutionalEdge = disciplinePremiumR > 0;

    return {
      totalNoTradeDecisions: totalNoTradeDecisions || 0,
      decisionsWithTracking: simulations.length,
      impulsivePnlR: parseFloat(impulsivePnlR.toFixed(2)),
      impulsivePnlUsd: parseFloat(impulsivePnlUsd.toFixed(2)),
      impulsiveWins,
      impulsiveLosses,
      impulsiveWinRate: parseFloat(impulsiveWinRate.toFixed(1)),
      actualPnlR: parseFloat(actualPnlR.toFixed(2)),
      actualPnlUsd: parseFloat(actualPnlUsd.toFixed(2)),
      disciplinePremiumR: parseFloat(disciplinePremiumR.toFixed(2)),
      disciplinePremiumUsd: parseFloat(disciplinePremiumUsd.toFixed(2)),
      avoidedDrawdownPercent: parseFloat(avoidedDrawdownPercent.toFixed(1)),
      lossesAvoided,
      totalLossAvoidedUsd: parseFloat(totalLossAvoidedUsd.toFixed(2)),
      hasInstitutionalEdge,
    };
  } catch (err) {
    console.error('[ImpulsiveTraderService] Exception calculating stats:', err);
    return null;
  }
}
